import { ftClockCampaignField as ftCampaignField } from '../shared/campaign-clock';
import { encounterCreatureField, encounterField, itemsCol, posCoord } from '../shared/array-types';
import { rowScalar, itemIdOf, statusField } from '../encounter-map/ft3d/perception';
import { COMPOUND_LINK_STATUSES, statusItemId } from '../janus-engine/inventory/lift-occupancy';
import { planPartyRelocation, partyRelocationBoundary } from '../janus-engine/movement/party-relocation';
import { geometryRowId, relocationShape, relocationContext, transferZoneRemovals, relocationDestination, relocationPoint, relocationSelection } from '../janus-engine/geometry/relocation-inputs';
import { routeLiftWorld, freshCarriedIds, planHeldInventoryPlacement } from './inventory-floor-move-truth';
import { hydrateInventoryDefinitions, inventoryDefinition } from '../janus-engine/inventory/definition';
import { readInventoryRows } from '../janus-engine/compile/inventory-raw-readers';
import { fetchAndCacheObject } from './object-cache';
import { hydrateReferencedDefinitions } from './ability-truth';
import { actorEncounterFor, waitForActorMovement } from './movement-truth';
import { waitForSavedInstall } from './gm-boundary-settle';
import { handleEncounterUpdate } from './encounter-update';
import { requireEndedTurn } from '../janus-engine/statuses/gm-boundary';
import { resolveGmRelocation, previewGmTransferExitEffects } from './gm-boundary-truth';
import { refuseUnported } from '../janus-engine/unported';
import { compileFetchedEntity } from '../shared/compile-fetched-entity';
import type { InventorySharedCatalog } from '../janus-engine/compile/inventory-shared-catalog';
import { nearbySpeech } from '../janus-engine/geometry/speech';
import { creatureBoxDistance } from '../janus-engine/geometry/creature-box-distance';
import { campaignArrivalDestination, parseCampaignSessionUtc } from '../janus-engine/movement/campaign-arrival';
import { retainHeldBase } from '../janus-engine/ability/frame-snapshot';
import { storedStatusRows } from '../janus-engine/compile/creature-status-reader';
import { prepareRelocationMembers } from './relocation-members';
import type { RelocationMemberFailure } from '../janus-engine/compile/saved-creature-relocation';
import { adoptSavedGeometryRelocation, publishGeometryRelocation } from '../shared/geometry-relocation-delivery';
import { withAsyncDeadline } from '../shared/async-deadline';
type Row = Record<string, unknown>;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const list = (v: unknown): Row[] => Array.isArray(v) ? v as Row[] : [];
const scalar = rowScalar;
type ArrivalDeparture = { source: Row; destinations: Map<string, string>; ownerId: string };
/** Storage ingresses (join and attendance) ask the native compound planner before their single save. */
export async function arrivalPlacementPlan(input: Row, batch?: ArrivalDeparture[]): Promise<Row> {
  const arrival = input.arrival_campaign ? campaignArrivalDestination(input.arrival_campaign as Row,
    parseCampaignSessionUtc(input.server_utc_now) ?? Date.now()) : null;
  if (arrival && !arrival.destinationId) return {};
  if (arrival && (upper(input.destination_id) !== arrival.destinationId || !input.destination_input)) {
    const target = actorEncounterFor(arrival.destinationId)
      ?? await fetchAndCacheObject(arrival.destinationId, { fresh: true }) as Row | null;
    if (!target) throw new Error('arrival:destination-unavailable');
    const anchor = scalar(target, 'inventory_id') ? target : null;
    const destination = anchor ? actorEncounterFor(upper(scalar(anchor, 'possess_id')))
      ?? await fetchAndCacheObject(upper(scalar(anchor, 'possess_id')), { fresh: true }) : target;
    input = { ...input, destination_input: destination, destination_row: anchor };
  }
  const raw = input.destination_input;
  if (!raw || !Array.isArray(input.source_ids)) throw new Error('arrival:inputs-unavailable');
  const envelope = (raw as Row)._entity_base as { entity_id?: string } | undefined;
  const dest = actorEncounterFor(upper(envelope?.entity_id ?? encounterField(raw as Row, 'encounter_id')))
    ?? await compileFetchedEntity(raw, input.entity_catalog as InventorySharedCatalog | undefined) as Row;
  if (!Array.isArray(dest.encounter_inventories_arr)) await window.ftAdoptInventoryBase?.(dest, { transient: true });
  const ids = input.source_ids.map(upper);
  if (!ids.length) return {};
  const members = await Promise.all(ids.map(async id => {
    const row = await fetchAndCacheObject(id, { fresh: true }) as Row | null;
    if (!row) throw new Error('arrival:actor-unavailable');
    return row;
  }));
  const sources = await Promise.all([...new Set(members.map(r => upper(scalar(r, 'encounter_id'))).filter(Boolean))].map(id => held(id)));
  const source = {
    encounter_creatures_arr: [...new Map(sources.flatMap(e => list(e.encounter_creatures_arr)).concat(members).map(r => [geometryRowId(r), r])).values()],
    encounter_inventories_arr: sources.flatMap(e => list(e.encounter_inventories_arr)) };
  const all = retainHeldBase(dest, { ...dest, // dnr-clone: allow size=one shallow planning root; why=borrow held geometry/definitions and replace only roster indices, never clone members
    catalog: Object.assign({}, dest.catalog, ...sources.map(e => e.catalog), ...members.map(r => r.catalog)),
    encounter_creatures_arr: source.encounter_creatures_arr.concat(list(dest.encounter_creatures_arr)),
    encounter_inventories_arr: source.encounter_inventories_arr.concat(list(dest.encounter_inventories_arr)) });
  const selectionSource: Row = { catalog: all.catalog, encounter_creatures_arr: source.encounter_creatures_arr,
    encounter_inventories_arr: source.encounter_inventories_arr };
  await hydrateInventoryDefinitions(readInventoryRows(all, 'encounter_inventories_arr'), all.catalog, fetchAndCacheObject);
  const shape = relocationShape(dest);
  const anchor = input.destination_row as Row | undefined;
  const anchorPosition = anchor?.position ?? anchor?.inventory ?? anchor;
  const point = anchor && scalar(anchor, 'inventory_id') ? {
    x: posCoord(anchorPosition, 'x_pos') ?? 0, y: posCoord(anchorPosition, 'y_pos') ?? 0, z: posCoord(anchorPosition, 'z_pos') ?? 0,
  } : undefined;
  const plan = planPartyRelocation({}, dest, shape, String(list(shape.boxes)[0]?.id ?? ''),
    routeLiftWorld(all, await freshCarriedIds(upper(encounterField(dest, 'encounter_id')), input.carried_creature_ids_by_encounter)).world,
    { source: selectionSource, ids, ...(point ? { point } : {}) });
  const moved = new Set(plan.placements.map(p => p.id));
  if (input.boundary !== true) for (const source of sources) requireEndedTurn(source, moved, 'transfer');
  if (plan.status !== 'placed') throw new Error(`arrival:${plan.reason ?? 'no-free-position'}`);
  // Automatic attendance/join callers require every requested cohort; they cannot silently omit an arrival.
  if (plan.refusals?.length) throw new Error(`arrival:${plan.refusals.join('; ')}`);
  const placements = Object.fromEntries(plan.placements.map(p => [p.id, { x: p.x, y: p.y, z: p.z,
    ...(arrival ? { destination_id: arrival.destinationId, arrival_reason: arrival.reason } : {}),
    ...(plan.cohorts?.[p.id] ? { members: plan.cohorts[p.id] } : {}) }]));
  const departures = batch ?? [];
  for (const source of sources) {
    const sourceId = upper(encounterField(source, 'encounter_id'));
    const destinations = new Map(plan.placements.filter(p => upper(p.source_encounter_id) === sourceId)
      .map(p => [p.id, upper(encounterField(dest, 'encounter_id'))]));
    const ownerId = ids.find(id => list(source.encounter_creatures_arr).some(row => geometryRowId(row) === id));
    if (ownerId && destinations.size) departures.push({ source, destinations, ownerId });
  }
  if (!batch) finishArrivalDepartures(placements, departures);
  return placements;
}
/** One source decision for the whole save, so sequential join/attendance calls cannot strip companions' mutual zones. */
function finishArrivalDepartures(placements: Row, departures: ArrivalDeparture[]): void {
  const destinations = new Map<string, string>(), sources = new Map<string, ArrivalDeparture>();
  for (const departure of departures) {
    for (const [id, dest] of departure.destinations) {
      if (destinations.has(id) && destinations.get(id) !== dest) throw new Error('arrival:conflicting-compound-destinations');
      destinations.set(id, dest);
    }
    const id = upper(encounterField(departure.source, 'encounter_id'));
    if (!sources.has(id)) sources.set(id, departure);
  }
  for (const { source, ownerId } of sources.values()) {
    const status_removals = transferZoneRemovals([source], destinations);
    if (!status_removals.length) continue;
    const logs = previewGmTransferExitEffects([source], status_removals, 'encounter-transfer');
    (placements[ownerId] as Row).departure = { status_removals, logs };
  }
}
Object.assign(window, { ftPlanArrivalPlacement: arrivalPlacementPlan });

export async function arrivalPlacementPlans(inputs: Row[]): Promise<Row> {
  const placements: Row = {}, batch: ArrivalDeparture[] = [];
  for (const input of inputs) Object.assign(placements, await arrivalPlacementPlan(input, batch));
  finishArrivalDepartures(placements, batch);
  return placements;
}

export async function campsiteInventoryPlan(data: Row): Promise<Row> {
  const f = await facts(data), campaign = f.campaign as Row;
  const previous = itemIdOf(ftCampaignField(campaign, 'campaign_campsite_encounter_id'));
  const destination = upper(data.encounter_id);
  if (!previous || previous === destination) return {};
  const [source, dest] = await Promise.all([held(previous, [], true), held(destination, [], true)]);
  const rows = readInventoryRows(source, 'encounter_inventories_arr');
  await hydrateInventoryDefinitions(rows, source.catalog, fetchAndCacheObject);
  dest.catalog = { ...(dest.catalog as Row), ...(source.catalog as Row) };
  const world = routeLiftWorld(dest).rest;
  return Object.fromEntries(rows.map(row => {
    const result = planHeldInventoryPlacement(dest, row, world, { x: posCoord(row, 'x_pos') ?? 0,
      y: posCoord(row, 'y_pos') ?? 0, z: posCoord(row, 'z_pos') ?? 0 });
    return [geometryRowId(row), result.position];
  }));
}
const host = (): Window & { ftCampaignCsrfToken?: string; ftMovementDispatchContext?: (id: string) => { world?: { shape?: unknown } } } => window as never;
async function facts(data: Row): Promise<Row> {
  // Reads the destination encounter's base (query_base_encounter): 8–20 s for a large campaign encounter (2026-10-25).
  const response = await fetch('/options/api/geometry_relocation_read_inc.php',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(data),signal:AbortSignal.timeout(90000)});
  const result = await response.json() as Row;
  if (!response.ok || result.ok !== true) throw new Error(String(result.error ?? 'relocation:read-unavailable'));
  if (Array.isArray(result.encounter_inputs)) result.encounter_inputs = await Promise.all(result.encounter_inputs
    .map(input => compileFetchedEntity(input, result.entity_catalog as InventorySharedCatalog | undefined)));
  return result;
}
// Only this page's confirmed saves owe a settle barrier. First sends keep the ordinary cache path.
const relocationWatermarks = new Map<string, { marks: Map<string, Row>; expires: number }>();
const RELOCATION_MARK_TTL_MS = 10000;
function rememberRelocation(destination: string, placements: Row[]): void {
  for (const p of placements) {
    for (const id of new Set([upper(p.source_encounter_id), destination])) {
      if (!id || p.unplaced === true && id !== destination || !actorEncounterFor(id)) continue;
      const prior = relocationWatermarks.get(id);
      const marks = new Map(prior && prior.expires > Date.now() ? prior.marks : undefined);
      marks.set(upper(p.id), {...p, destination});
      relocationWatermarks.delete(id);
      relocationWatermarks.set(id, { marks, expires: Date.now() + RELOCATION_MARK_TTL_MS });
      if (relocationWatermarks.size > 32) relocationWatermarks.delete(relocationWatermarks.keys().next().value!);
    }
  }
}
function relocationInstalled(encounter: Row, id: string, marks: Map<string, Row>): boolean {
  return [...marks].every(([memberId, p]) => {
    const rows = encounter[p.type === 'creature' ? 'encounter_creatures_arr' : 'encounter_inventories_arr'];
    if (!Array.isArray(rows)) return false;
    const row = list(rows).find(r => geometryRowId(r) === memberId);
    const rev = row && Number(scalar(row, 'movement_rev') ?? 0);
    if (p.destination !== id) return !row || p.type === 'creature' && Number(rev) > Number(p.rev);
    if (!row) return false;
    if (p.type === 'creature') return Number(rev) >= Number(p.rev);
    // Inventory saves have no movement revision; their saved location is the watermark.
    return upper(scalar(row, 'possess_id')) === id && (['x', 'y', 'z'] as const).every(axis => Math.abs((posCoord(row, `${axis}_pos`) ?? 0) - Number(p[axis])) < .001);
  });
}
/** A room this page does not hold, read fresh. A save landing while the read assembles its scope answers 409, and a contended adoption
 *  can time out; either comes back null. Read it again before refusing, and name a read that never answers rather than the room. */
async function freshRoom(id: string): Promise<Row> {
  for (let attempt = 0; ; attempt++) {
    const started = Date.now(), value = await fetchAndCacheObject(id, {fresh:true}) as Row | null;
    if (value && Array.isArray(value.encounter_creatures_arr)) return value;
    // A read that failed slowly (a timed-out adoption) will not answer faster on the next try.
    if (attempt === 2 || Date.now() - started > 20000) throw new Error('relocation:room-read-failed');
    await new Promise(resolve => setTimeout(resolve, 500 * 3 ** attempt));
  }
}
async function held(id: string, inputs: Row[] = [], fresh = false): Promise<Row> {
  const supplied = inputs.find(e => upper(encounterField(e,'encounter_id'))===id);
  const pending = fresh ? relocationWatermarks.get(id) : undefined;
  let value = supplied ?? actorEncounterFor(id);
  if (pending && !supplied && value && !relocationInstalled(value, id, pending.marks)) {
    const recover = async () => {
      // A forced read starts after this save, even if the ordinary resync already has an older read in flight.
      const current = await freshRoom(id);
      await handleEncounterUpdate({type:'encounter_update',encounter_arr:current as Parameters<typeof handleEncounterUpdate>[0]['encounter_arr'],
        version:typeof current._ws_version === 'number' ? current._ws_version : undefined});
      value = actorEncounterFor(id);
      if (!value) throw new Error('relocation:encounter-not-held');
    };
    if (pending.expires <= Date.now()) await recover();
    else await waitForSavedInstall({
      installed: () => {
        value = actorEncounterFor(id);
        return !value || relocationInstalled(value, id, pending.marks);
      }, recover, pollMs: 50,
    });
  }
  value ??= await freshRoom(id);
  if (!Array.isArray((value as Row).encounter_creatures_arr)) throw new Error('relocation:encounter-not-held');
  // Only the planning root and cold floor rows are writable. Held members, definitions and warm floor objects stay borrowed.
  value = retainHeldBase(value as Row, { ...value });
  const floor = list(value.encounter_inventories_arr);
  if (floor.some(row => !inventoryDefinition(row, value!.catalog))) {
    const original = value.encounter_inventories_arr;
    value.encounter_inventories_arr = floor.map(row => inventoryDefinition(row, value!.catalog) ? row : retainHeldBase(row, { ...row }));
    if (value.inventory === original) value.inventory = value.encounter_inventories_arr;
  }
  if (fresh) {
    if (!Array.isArray(value.encounter_inventories_arr)) await window.ftAdoptInventoryBase?.(value, { transient: true });
    if (!Array.isArray(value.encounter_inventories_arr)) throw new Error('relocation:inventory-unavailable');
    // A fresh server snapshot supersedes marks even when another lane removed/moved their members.
    if (pending && relocationWatermarks.get(id) === pending && (!supplied ||
      relocationInstalled(actorEncounterFor(id) ?? {}, id, pending.marks))) relocationWatermarks.delete(id);
  }
  return value;
}
/** An unplaced PC has no source encounter. Its persisted parent, revision and position remain the save's CAS. */
async function unplacedRow(raw: Row): Promise<Row> {
  return persisted(await ownBody(geometryRowId(raw)), raw);
}
async function ownBody(id: string): Promise<Row> {
  const value = await fetchAndCacheObject(id,{fresh:true}) as Row | null;
  if (!value || !value.encounter_creatures || typeof value.encounter_creatures !== 'object') throw new Error('relocation:unplaced-body-unavailable');
  return retainHeldBase(value as Row, { ...value });
}
function persisted(row: Row, raw: Row): Row {
  row.encounter_creatures = {...(row.encounter_creatures as Row),encounter_id:raw.encounter_id ?? null,movement_rev:Number(raw.movement_rev ?? 0)};
  row.position = {x_pos:Number(raw.x_pos ?? 0),y_pos:Number(raw.y_pos ?? 0),z_pos:Number(raw.z_pos ?? 0)};
  return row;
}
/** True when a mover's own compiled body proves it leaves its room alone: no compound link touches it from either side (the planner's
 *  cohort graph is undirected — compoundActorIds), it anchors no zone and holds no zone membership (transferZoneRemovals' inputs), and it
 *  is one living identity. The compile omits an empty status bucket, and its base always reads both sides (the from-base stages refuse
 *  one without them), so a missing bucket holds none. A connected status is a membership only when a live zone anchor shares its
 *  connection id (`anchors`; most connected statuses have none); without that list every connected status counts. */
export function moverLeavesAlone(body: Row, anchors: ReadonlySet<string> | null = null,
  cohort?: ReadonlySet<string>, carriedAnchors?: ReadonlySet<string>): boolean {
  const id = geometryRowId(body), alias = upper(Array.isArray((body.inventory as Row | undefined)?.inventory_id)
    ? ((body.inventory as Row).inventory_id as unknown[])[0] : (body.inventory as Row | undefined)?.inventory_id);
  if (!id || (alias && alias !== id) || Number(scalar(body, 'encounter_creature_dead') ?? 0) === 1) return false;
  for (const s of ftCreatureStatuses(body, 'target') as Row[]) {
    const connection = upper(statusField(s, 'status_connected_id'));
    if (Number(statusField(s, 'status_aoe_source') ?? 0) === 1 && !cohort
      || connection && (!anchors || anchors.has(connection)) && !carriedAnchors?.has(connection)) return false;
    const caster = upper(statusField(s, 'status_caster_id'));
    if (COMPOUND_LINK_STATUSES.has(statusItemId(s)) && caster && caster !== id && !cohort?.has(caster)) return false;
  }
  for (const s of ftCreatureStatuses(body, 'caster') as Row[])
    if (COMPOUND_LINK_STATUSES.has(statusItemId(s)) && upper(statusField(s, 'status_target_id')) !== id
      && !cohort?.has(upper(statusField(s, 'status_target_id')))) return false;
  return true;
}
/** Independent movers need their own bodies and the saved turn pointer, rather than a compile of their entire source room. */
async function leanSources(candidates: string[], placed: Row[], reread: () => Promise<Row>, destination: Row): Promise<{ rooms: Map<string, Row>; failures: readonly RelocationMemberFailure[] }> {
  const rooms = new Map<string, Row>(), mode: Record<string, string> = {};
  // Read after the movement waits, as the room itself would be: a turn can pass to a mover without its row changing.
  const fresh = candidates.length ? reread().catch(() => ({} as Row)) : Promise.resolve({} as Row);
  const movers = placed.filter(row => candidates.includes(upper(scalar(row, 'encounter_id'))));
  const base = destination._entity_base as { base?: Row } | undefined;
  const bodies = movers.length ? prepareRelocationMembers(movers.map(geometryRowId), base?.base ?? destination) : Promise.resolve({ rows: new Map<string, Row>(), failures: [] });
  const prepared = await bodies, failed = new Set(prepared.failures.map(failure => failure.id));
  await Promise.all(candidates.map(async id => {
    const movers = placed.filter(r => upper(scalar(r, 'encounter_id')) === id && !failed.has(geometryRowId(r)));
    if (!movers.length) { rooms.set(id, { encounters: { encounter_id: id }, encounter_creatures_arr: [] }); return; }
    const f = await fresh, turn = list(f.turns).find(r => upper(r.encounter_id) === id);
    const anchors = Array.isArray(f.zone_anchors) ? new Set((f.zone_anchors as unknown[]).map(upper)) : null;
    const compiled = prepared.rows;
    if (!turn) { mode[id] = 'no-turn-pointer'; return; }
    const cohort = new Set(movers.map(geometryRowId));
    const wholeSource = list(f.creatures).filter(row => upper(scalar(row, 'encounter_id')) === id).every(row => cohort.has(geometryRowId(row)));
    const carriedAnchors = new Set(movers.flatMap(row => (ftCreatureStatuses(compiled.get(geometryRowId(row)) ?? {}, 'target') as Row[])
      .filter(status => Number(statusField(status, 'status_aoe_source') ?? 0) === 1).map(status => upper(statusField(status, 'status_connected_id')))));
    const found = movers.map(r => {
      const body = compiled.get(geometryRowId(r));
      // The read row predates this page's movement waits: a body saved since (another revision or room) loads the room instead.
      if (!body || upper(scalar(body, 'encounter_id')) !== id || Number(scalar(body, 'movement_rev') ?? 0) !== Number(scalar(r, 'movement_rev') ?? 0)) return 'body-not-current';
      return moverLeavesAlone(body, anchors, wholeSource ? cohort : undefined, carriedAnchors) ? persisted(body, r) : 'tied-to-room';
    });
    const refused = found.find(body => typeof body === 'string');
    if (refused) { mode[id] = refused; return; }
    mode[id] = 'lean';
    rooms.set(id, { encounters: { ...turn }, encounter_creatures_arr: found, catalog: Object.assign({}, ...found.map(row => (row as Row).catalog)) });
  }));
  (window as Window & { __ftRelocationSourceMode?: Record<string, string> }).__ftRelocationSourceMode = mode;
  return { rooms, failures: prepared.failures };
}
/** The party panel names one PC by item: search the unplaced rows too. An unreachable PC still names itself, so a missing
 *  source is distinguished from an empty selection. The read includes recoverable deleted rooms in its unplaced rows. */
function selection(data: Row, f: Row): string[] {
  if (data.action === 'haven-return' || Array.isArray(data.creature_ids) || !data.player_item_id) return relocationSelection(data,f);
  const pc = upper(data.player_item_id), found = [...list(f.creatures),...list(f.unplaced)].filter(r => upper(scalar(r,'item_id')) === pc).map(geometryRowId);
  return found.length ? found : [pc];
}
/** Warm possible landing carriers and their batched references alongside relocation planning.
 *  The boundary prepares any still-missing inputs only when a support consequence reads its carrier. */
function warmBoundaryCarriers(): void {
  void Promise.all(['BE42824F-46BE-EC80-AB7F-B823190AB6C8', 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA'].map(id => fetchAndCacheObject(id).catch(() => null)))
    .then(definitions => hydrateReferencedDefinitions({}, {}, definitions.filter((def): def is NonNullable<typeof def> => def !== null)))
    .catch(() => {});
}
/** Shared form, member-map, and generic GM tools adapter. The legacy endpoint is never called. */
export async function geometryRelocation(data: Row, recoveredSources?: Row[]): Promise<Row> {
  const f = await facts(data), campaign = f.campaign as Row;
  if (data.action === 'context') {
    const seed = list(f.creatures).find(r => geometryRowId(r) === upper(data.creature_id));
    if (!seed) throw new Error('relocation:creature-unavailable');
    const sourceId = upper(scalar(seed,'encounter_id')), destination = upper(data.destination_encounter_id) || sourceId;
    if (!list(f.encounters).some(row => upper(encounterField(row, 'encounter_id')) === destination)) throw new Error('relocation:destination-not-in-campaign');
    const source = await held(sourceId), dest = destination === sourceId ? source : await held(destination);
    return relocationContext(list(f.encounters), source, dest);
  }
  const destination = relocationDestination(data,f), ids = selection(data,f);
  if (!destination || !ids.length) throw new Error('relocation:nothing-to-move');
  if (!list(f.encounters).some(r => upper(encounterField(r,'encounter_id'))===destination)) throw new Error('relocation:destination-not-in-campaign');
  const unplacedIds = new Set(list(f.unplaced).map(geometryRowId));
  const seeds = [...list(f.creatures),...list(f.unplaced)].filter(r => ids.includes(geometryRowId(r)));
  if (seeds.length !== new Set(ids).size) throw new Error('relocation:source-not-in-campaign');
  const placed = seeds.filter(r => !unplacedIds.has(geometryRowId(r)));
  const sources = [...new Set(placed.map(r => upper(scalar(r,'encounter_id'))))];
  for (const seed of placed) await waitForActorMovement(upper(scalar(seed,'encounter_id')),geometryRowId(seed));
  const inputs = (recoveredSources ?? ([] as Row[])).concat(list(f.encounter_inputs)), handed = (id: string): boolean => inputs.some(e => upper(encounterField(e,'encounter_id')) === id);
  const candidates = sources.filter(id => id !== destination && !actorEncounterFor(id) && !handed(id));
  // Wait only for this page's outstanding saved revisions; ordinary room caches need no network read.
  const room = new Map<string, Promise<Row>>([...new Set([destination,...sources])].filter(id => !candidates.includes(id)).map(id => [id,held(id,inputs,true)]));
  // The destination's carried set and its floor definitions depend on nothing the movers' bodies say: read them meanwhile. The pass
  // over every room below then finds the destination's rows pinned, or joins their in-flight reads.
  const carriedIds = freshCarriedIds(destination);
  // (A failed room read surfaces from the room's own promise below.)
  void room.get(destination)!.then(d => hydrateInventoryDefinitions(readInventoryRows(d,'encounter_inventories_arr'),d.catalog,fetchAndCacheObject)).catch(() => {});
  const [prepared, loose] = await Promise.all([leanSources(candidates, placed, () => facts(data), await room.get(destination)!),
    Promise.all(seeds.filter(r => unplacedIds.has(geometryRowId(r))).map(unplacedRow)), ...room.values()]);
  const lean = prepared.rooms, refused = new Set(prepared.failures.map(failure => failure.id));
  const admitted = ids.filter(id => !refused.has(id));
  if (prepared.failures.length) {
    const reasons = prepared.failures.map(failure => {
      const member = seeds.find(row => geometryRowId(row) === failure.id);
      const name = encounterCreatureField(member, 'encounter_creature_name') || itemsCol(member, 'item_name') || 'Creature';
      return `${String(name)}: ${failure.reason}`;
    }).join('; ');
    // Only an explicit GM move may leave refused members behind; evacuation must finish before source deletion.
    if (!/^gm_(?:pull|push|move)_/.test(String(data.relocation_route ?? '')) || !admitted.length)
      throw new Error(`relocation:members-refused:${reasons}`);
    window.displayWarning?.('Could not move: ' + reasons);
  }
  for (const id of candidates) room.set(id, lean.has(id) ? Promise.resolve(lean.get(id)!) : held(id,inputs,true));
  // Source order with the destination where it first appears, as before: later catalogs win the merge below.
  const encounters = await Promise.all([...new Set([...sources,destination])].filter(id => room.has(id)).map(id => room.get(id)!));
  const dest = await room.get(destination)!;
  const source = {...dest, encounter_creatures_arr:[...encounters.filter(e => sources.includes(upper(encounterField(e,'encounter_id')))).flatMap(e => list(e.encounter_creatures_arr)),...loose],
    encounter_inventories_arr:encounters.filter(e => sources.includes(upper(encounterField(e,'encounter_id')))).flatMap(e => list(e.encounter_inventories_arr))};
  const all = {...dest,catalog:Object.assign({},...encounters.map(e=>e.catalog),...loose.map(r=>r.catalog)),encounter_creatures_arr:[...new Map([...encounters.flatMap(e => list(e.encounter_creatures_arr)),...loose].map(r => [geometryRowId(r),r])).values()],
    encounter_inventories_arr:[...new Map(encounters.flatMap(e => list(e.encounter_inventories_arr)).map(r => [geometryRowId(r),r])).values()]};
  await hydrateInventoryDefinitions(readInventoryRows(all, 'encounter_inventories_arr'), all.catalog, fetchAndCacheObject);
  const shape = relocationShape(dest,list(f.doors).filter(r => upper(r.encounter_id)===destination));
  if(data.relocation_route==='gm_move_creature_cross_encounter_at_grid_inc.php')data={...data,x:data.x_pos,y:0,z:data.z_pos};
  dest.encounters={...(dest.encounters as Row),encounter_shape:JSON.stringify(shape)};
  const point = relocationPoint(data, f);
  const roomId = String(data.room_id ?? list(shape.boxes)[0]?.id ?? '');
  // The planner judges every destination resident against an unbounded probe, so a dead row whose body the page cannot place
  // (no corpse row on this floor) would refuse the whole move as lift-body-carried. The destination's carried set answers it exactly;
  // the obstacle pass reads only destination residents (source rows' `carried` is never consulted). A failed read keeps them uncertain.
  const carried = await carriedIds;
  const plan = planPartyRelocation(campaign,dest,shape,roomId,routeLiftWorld(all,carried).world,{source,ids:admitted,...(point?{point}:{})});
  if (plan.reason === 'nothing_to_move' && seeds.length === admitted.length
    && seeds.every(row => upper(scalar(row, 'encounter_id')) === destination)) {
    return adoptSavedGeometryRelocation({ campaign_id: itemIdOf(ftCampaignField(campaign, 'campaign_id')),
      encounter_id: destination, actor_id: f.actor_id,
      campaign_update: { after: itemIdOf(ftCampaignField(campaign, 'encounter_loaded')) },
      placements: seeds.map(row => ({ type: 'creature', id: geometryRowId(row), rev: Number(scalar(row, 'movement_rev') ?? 0),
        x: posCoord(row, 'x_pos') ?? 0, y: posCoord(row, 'y_pos') ?? 0, z: posCoord(row, 'z_pos') ?? 0 })) });
  }
  if (plan.status !== 'placed' || !plan.placements.length) throw new Error(`relocation:${plan.reason ?? 'formation-blocked'}`);
  // Evacuation cannot delete a source that still holds a refused group. Explicit pushes can move the others.
  if (plan.refusals?.length && data.relocation_route === EVACUATE_ROUTE) throw new Error(`relocation:${plan.refusals.join('; ')}`);
  if (upper(f.actor_id) && (plan.placements.length !== 1 || plan.placements[0]?.type !== 'creature' || plan.placements[0]?.id !== upper(f.actor_id)))
    throw new Error('relocation:own-player-only');
  const moved = new Set(plan.placements.filter(p => p.source_encounter_id!==destination).map(p => p.id));
  for (const e of encounters) requireEndedTurn(e,moved,'transfer');
  const removals = transferZoneRemovals(encounters.filter(e => upper(encounterField(e,'encounter_id'))!==destination),moved);
  const logs=previewGmTransferExitEffects(encounters.filter(e=>upper(encounterField(e,'encounter_id'))!==destination),removals);
  const campaignBefore=itemIdOf(ftCampaignField(campaign,'encounter_loaded'));
  const route=String(data.relocation_route??'');
  const loadCampaign=route.startsWith('gm_push_') || route==='go_to_encounter_inc.php' && !upper(f.actor_id);
  const explore=plan.placements.some(p=>p.type==='creature' && list(source.encounter_creatures_arr).some(r=>geometryRowId(r)===p.id&&itemsCol(r,'item_type')==='player_character'));
  // An unplaced PC's placement says so: the save locks it by its own persisted parent (NULL or a dangling id), not a source encounter.
  const placements = plan.placements.map(p => unplacedIds.has(p.id) ? {...p,unplaced:true} : p);
  const command = {campaign_id:itemIdOf(ftCampaignField(campaign,'campaign_id')),actor_id:f.actor_id??'',campaign_update:loadCampaign?{before:campaignBefore,after:destination}:null,
    explored:explore?{before:Number(encounterField(dest,'encounter_explored')??0),after:1}:null,encounter_id:destination,operation_id:crypto.randomUUID().toUpperCase(),csrf_token:host().ftCampaignCsrfToken ?? f.csrf_token ?? data.csrf_token,placements,status_removals:removals,logs};
  let answer: Row | null = null;
  let savedResponse: Response | undefined;
  for (let attempt=0;attempt<3;attempt++) {
    try {
      const response=await withAsyncDeadline(() => fetch('/options/game/actions/geometry_relocation_truth_commit_inc.php',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(command),signal:AbortSignal.timeout(20000)}), 20000);
      savedResponse=response;
      answer=await withAsyncDeadline(() => response.json(), 20000) as Row;
      if (response.ok && answer.ok===true) break;
      // This exact CAS refusal rolls back the whole transaction. Refresh only its source rooms and re-plan once;
      // a timeout, relay failure or any other refusal may already have saved, so never start a new command for those.
      if (response.status === 409 && answer.error === 'relocation:stale' && !recoveredSources)
        return geometryRelocation(data, await Promise.all(sources.map(freshRoom)));
      if (response.status<500) throw new Error(String(answer.error ?? 'relocation:save-refused'));
    } catch(error) { if (attempt===2 || error instanceof Error && error.message.startsWith('relocation:')) throw error; }
  }
  if (answer?.ok!==true) throw new Error('relocation:save-unconfirmed');
  await publishGeometryRelocation({ ...command, placements: answer.placements ?? command.placements }, source, encounters, savedResponse!);
  if (plan.refusals?.length) window.displayWarning?.(relocationFailureMessage(`relocation:${plan.refusals.join('; ')}`));
  rememberRelocation(destination, list(answer.placements));
  const arrived=retainHeldBase(dest,{...dest});
  for (const type of ['creature','inventory'] as const) {
    const incoming=plan.placements.filter(p=>p.type===type);
    if(!incoming.length)continue;
    const key=type==='creature'?'encounter_creatures_arr':'encounter_inventories_arr';
    const originals=new Map(list(source[key]).map(r=>[geometryRowId(r),r]));
    const residents=new Map(list(dest[key]).map(r=>[geometryRowId(r),r]));
    for(const p of incoming) {
      const original=originals.get(p.id);
      if(!original)throw new Error('party-relocation:row-unavailable');
      residents.delete(p.id); residents.set(p.id,original);
    }
    arrived[key]=[...residents.values()];
    if(type==='inventory' && dest.inventory===dest[key])arrived.inventory=arrived[key];
  }
  const boundary:Row={...partyRelocationBoundary(arrived,plan.placements),actor_id:upper(f.actor_id)||destination}; boundary.transfer=moved.size>0;
  const after=boundary.after as Row;
  const deleted=new Set(removals.map(r=>r.status_id));
  const residents=list(after.encounter_creatures_arr);
  let creatures=residents;
  let ownsCreatures=plan.placements.some(p=>p.type==='creature');
  for(const [index,member] of creatures.entries()) {
    // Placement already owns this moved row's writable bucket.
    if(moved.has(geometryRowId(member)))(member.encounter_creatures as Row).encounter_id=destination;
    const statuses=member.statuses as Row|undefined;
    let patch:Row|undefined;
    if(statuses && deleted.size) for(const side of ['target','caster'] as const) {
      const rows=storedStatusRows(member,side), kept=rows.filter(s=>!deleted.has(upper(statusField(s,'status_id'))));
      if(kept.length!==rows.length)(patch??={...statuses})[side]=kept;
    }
    if(patch) {
      if(!ownsCreatures) { after.encounter_creatures_arr=creatures=residents.slice(); ownsCreatures=true; }
      creatures[index]=retainHeldBase(member,{...member,statuses:patch});
    }
  }
  const carriesZone = (row: Row): boolean => (ftCreatureStatuses(row, 'target') as Row[])
    .some(status => Number(statusField(status, 'status_aoe_source') ?? 0) === 1);
  const zoneDue = carriesZone(after) || list(after.encounter_creatures_arr).some(carriesZone) || list(after.encounter_inventories_arr).some(carriesZone);
  if (zoneDue) { warmBoundaryCarriers(); await resolveGmRelocation(boundary); }
  return answer;
}
/** A governed-source denial is an ordinary refusal, not a missing browser capability. */
export function reportRelocationAuthorityRefusal(error: unknown): boolean {
  const reason = error instanceof Error ? error.message : String(error);
  if (reason !== 'relocation:source-not-in-campaign') return false;
  window.displayWarning?.('That character is not in this campaign.');
  return true;
}
/** Keep the diagnostic key while giving every relocation surface the same actionable explanation. */
export function relocationFailureMessage(reason: string): string {
  if (reason === 'relocation:stale' || reason === 'relocation:stale-source')
    return 'A creature moved while this transfer was being saved. Wait for its movement to finish, then try again.';
  if (reason === 'relocation:nothing-to-move')
    return 'There are no eligible creatures in the source encounter. Check the current encounter and try again.';
  if (reason === 'relocation:room-read-failed' || reason === 'relocation:read-unavailable')
    return 'The encounter could not be loaded just now. Try again in a moment.';
  if (reason.startsWith('relocation:members-refused:')) return 'Could not move: ' + reason.slice('relocation:members-refused:'.length);
  if (reason.startsWith('relocation:no-free-position:'))
    return reason.includes('formation_blocked')
      ? 'The encounter is full. No free floor for ' + reason.slice('relocation:no-free-position:'.length).replace(/no-free-position: /g, '').replace(/ \(formation_blocked\)/g, '').trim() + '.'
      : 'Could not find space for ' + reason.slice('relocation:no-free-position:'.length).trim() + '. Choose another arrival point.';
  if (/unported:|not-ported:|browser-not-loaded/.test(reason)) return 'Not yet supported in the browser: ' + reason;
  return 'The transfer could not finish. Try again or choose another arrival point. [' + reason + ']';
}
Object.assign(window,{ftGeometryRelocation:async(data:Row):Promise<Row>=>{
  try { return await geometryRelocation(data); }
  catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    const message = relocationFailureMessage(reason);
    if (!reportRelocationAuthorityRefusal(error)) refuseUnported(reason, undefined, message);
    return {ok:false,error:reason,message};
  }
}});
const EVACUATE_ROUTE = 'gm_evacuate_encounter_to_campaign_campsite';
const isHavenName = (row: Row): boolean => ['Campaign Campsite','Campaign Stronghold'].includes(String(encounterField(row,'encounter_name') ?? ''));
/** Rulings 25–26 (owner 2026-10-14): a campaign encounter's PCs move to the campaign campsite, browser-planned, BEFORE the
 *  encounter is deleted — one room ({item_type:'encounter', encounter_id}) or every non-haven room ({campaign_id}, mirroring
 *  delete_all_campaign_encounters.php's skips). A refusal stops the delete; nothing is ever stranded in a deleted room. */
async function evacuateEncounterPcs(data: Row): Promise<Row> {
  const one = upper(data.encounter_id);
  let campaignId = one ? '' : upper(data.campaign_id);
  if (one) {
    const encounter = await fetchAndCacheObject(one,{fresh:true}) as Row | null;
    if (!encounter) throw new Error('relocation:encounter-unavailable'); // an unreadable room is never deleted blind
    if (encounterField(encounter,'encounter_master_parent_type') !== 'campaign') {
      // Ruling 29: a room no campaign owns can still hold campaign PCs (link travel from a location-owned haven). Only their own
      // campaign GMs may move them, so the deleter is refused by name rather than stranding them.
      if (list(encounter.encounter_creatures_arr).some(r => String(scalar(r,'item_type') ?? '') === 'player_character')) throw new Error('relocation:campaign-pcs-inside');
      return {ok:true,moved:0,selected:0};
    }
    campaignId = upper(encounterField(encounter,'encounter_master_parent_id'));
  }
  if (!campaignId) return {ok:true,moved:0,selected:0};
  const f = await facts({campaign_id:campaignId}), campaign = f.campaign as Row;
  const campsite = itemIdOf(ftCampaignField(campaign,'campaign_campsite_encounter_id'));
  // Ruling 32: "The campaign always needs a campsite." Its current campsite is never deleted — the GM sets another first.
  if (one && one === upper(campsite)) throw new Error('campaign:campsite-required');
  const havens = new Set([campsite,itemIdOf(ftCampaignField(campaign,'campaign_stronghold_encounter_id'))].filter(Boolean));
  const rooms = new Set(one ? [one] : list(f.encounters).filter(r => !havens.has(upper(encounterField(r,'encounter_id'))) && !isHavenName(r))
    .map(r => upper(encounterField(r,'encounter_id'))));
  const pcs = list(f.creatures).filter(r => rooms.has(upper(scalar(r,'encounter_id'))) && Number(scalar(r,'creature_number') ?? 0) === 0).map(geometryRowId);
  if (!pcs.length) return {ok:true,moved:0,selected:0};
  if (!campsite) throw new Error('relocation:no-campsite');
  if (rooms.has(campsite)) throw new Error('relocation:evacuate-campsite');
  const answer = await geometryRelocation({campaign_id:campaignId,relocation_route:EVACUATE_ROUTE,creature_ids:pcs});
  // `moved` counts the receipt's saved PC placements; `selected` is what the delete must have moved — the caller refuses any shortfall.
  return {...answer,selected:pcs.length,moved:answer.ok === true ? list(answer.placements).filter(p => pcs.includes(upper(p.id))).length : 0};
}
/** Ruling 26: a PC attached to a campaign but in no room (a new character, or one whose room was deleted) is placed in that
 *  campaign's campsite by its owner or GM — a haven placement, browser-planned. Quiet: the caller reports the outcome. */
async function placeUnplacedInCampsite(campaignId: string, pcId: string): Promise<Row> {
  return geometryRelocation({action:'haven-return',haven:'campsite',campaign_id:upper(campaignId),actor_id:upper(pcId)});
}
const quiet = (run: () => Promise<Row>) => async (): Promise<Row> => {
  try { return await run(); } catch (error) { return {ok:false,error:error instanceof Error ? error.message : String(error)}; }
};
Object.assign(window,{
  ftEvacuateEncounterPcs:(data:Row):Promise<Row>=>quiet(()=>evacuateEncounterPcs(data))(),
  ftPlaceUnplacedPcInCampsite:(campaignId:string,pcId:string):Promise<Row>=>quiet(()=>placeUnplacedInCampsite(campaignId,pcId))(),
});
Object.assign(window,{ft3dSpeechPlan:async(actorId:string,encounterId:string,campaignId:string):Promise<Row>=>{
  const encounter=await held(encounterId);
  const actor=list(encounter.encounter_creatures_arr).find(row=>geometryRowId(row)===upper(actorId));
  if(!actor)throw new Error('speech:actor-not-held');
  const response=await fetch(`/options/api/ft3d_perception_inc.php?campaign_id=${encodeURIComponent(campaignId)}&encounter_id=${encodeURIComponent(encounterId)}&viewer_id=${encodeURIComponent(actorId)}`);
  const inputs=await response.json() as Row;
  if(!response.ok || inputs.ok!==true)throw new Error(String(inputs.error??'speech:geometry-unavailable'));
  const doors=Object.entries(inputs.doorStates as Row??{}).map(([opening_id,state])=>({opening_id,door_open:(state as Row).open===true?1:0}));
  const shape=relocationShape(encounter,doors);
  for(const opening of list(shape.openings))if(doors.some(d=>d.opening_id===opening.id))opening.transparent=opening.passable===true;
  return nearbySpeech(encounter,shape as never,actor,(a,b)=>creatureBoxDistance(a,b,true));
}});
